import { Injectable } from '@angular/core';

import { ImageProfile } from '@cds/betting-offer';
import { PrettyUrlsConfig, Sitecore, TimeFilterConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, children } from '@frontend/sports/common/core/data-access/sport-model';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { WidgetLayoutResponse } from '@frontend/sports/types/components/widget';
import { isArray, isInteger } from 'lodash-es';
import { Observable, isObservable, map } from 'rxjs';

import { BreadcrumbModel } from '../../breadcrumbs/breadcrumb.models';
import { BreadcrumbResult, BreadcrumbsResolve } from '../../breadcrumbs/breadcrumbs.resolver';
import { CalendarService } from '../../calendar/calendar.service';
import { SportsCacheService } from '../../client-caching/sports-cache.service';
import { getItemTypeForSport } from '../../competition-list/competition-list.models';
import { CompetitionRouteService } from '../../competition-list/competition-route.service';
import { CompetitionOverview } from '../../competition-list/services/competition-list.service';
import { FixtureList } from '../../event-list-shared/sport/competitions/competition.models';
import { SportUrlParam, UrlHelperService } from '../../navigation-core/url-helper.service';
import { CompetitionRoute } from '../../navigation/navigation.models';

type AppendFunction = (icon: string, title: string, url: string, sportId?: number, leagueId?: number, imageProfile?: ImageProfile) => number;
type CrumbFunction = (append: AppendFunction, data: CountItem, route: CompetitionRoute) => number | undefined;
type CrumbSource = FixtureList | CountItem | CompetitionOverview | WidgetLayoutResponse;

@Injectable()
export class EventListBreadcrumbsResolver extends BreadcrumbsResolve<CrumbSource> {
    constructor(
        private sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private urlConfig: PrettyUrlsConfig,
        private route: CompetitionRouteService,
        private calendar: CalendarService,
        private cache: SportsCacheService,
        private timeFilterConfig: TimeFilterConfig,
    ) {
        super();
    }

    protected customResolve(data: CrumbSource): BreadcrumbResult {
        const route = this.route.params();
        const sport = this.getType(data, route.sport);

        if (isObservable(sport)) {
            return sport.pipe(map((s) => this.map(route, s)));
        } else {
            return this.map(route, sport);
        }
    }

    private map = (params: CompetitionRoute, sport?: CountItem) => {
        const crumbs: BreadcrumbModel[] = [];

        const append: AppendFunction = (icon, title, url, sportId?, leagueId?, imageProfile?) => {
            if (sportId && leagueId) {
                const fallbackIcon = 'sports-icon sports-league';

                return crumbs.push({ icon, title, url, sportId, leagueId, fallbackIcon, imageProfile });
            }

            return crumbs.push({ icon, title, url });
        };

        const callbacks = params.conference ? [this.addLeague, this.addConference] : [this.addSport, this.addContext, this.addRegion, this.addLeague];

        if (sport) {
            for (const current of callbacks) {
                const index = current(append, sport, params);

                if (!isInteger(index)) {
                    break;
                }
            }

            // if not on Conference page AND
            // if only context was added (without calendar filter) we remove it from the crumbs
            const crumbsLengthBeforeContext = sport.isEsport ? 3 : 2;
            if (!params.conference && crumbs.length === crumbsLengthBeforeContext) {
                return crumbs.slice(0, crumbsLengthBeforeContext - 1);
            }
        }

        return crumbs;
    };

    private getType(data: CrumbSource, sport?: number): Observable<CountItem | undefined> | CountItem | undefined {
        if (!data) {
            return;
        }
        // FixtureList | CompetitionOverview
        else if ('sport' in data) {
            return data.sport!;
        }
        // CountItem
        else if ('type' in data && data.type === ItemType.Sport) {
            return data;
        }
        // WidgetLayout
        else if ('page' in data && sport) {
            return this.cache.getSport(sport);
        }

        return;
    }

    private addSport: CrumbFunction = (append, sport, route) => {
        const title = this.sitecore.seo.SportBreadcrumbTitle.replace(/{{sport}}/, sport.name);

        if (sport.isEsport) {
            append('theme-e-sports', this.sitecore.eSports.ESports, this.urlHelper.getESportsUrl());
        }

        return append(`sports-${sport.id}`, title, this.urlHelper.getSportUrl(sport, undefined, undefined, undefined, undefined, undefined, false));
    };

    private addContext: CrumbFunction = (append, sport, route) => {
        if (!route?.context) {
            return;
        }

        const interval = this.calendar.getInterval(route.context);
        const title = this.sitecore.globalMessages;
        const url = this.urlConfig.translations;

        if (route.context === url.betting) {
            return append('sports-globe', title.Competitions, this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, SportUrlParam.Competitions));
        }

        if ((route.context === url.calendar || interval) && !this.timeFilterConfig.isTimeFilterEnabled) {
            let result = append('sports-calendar', title.Calendar, this.urlHelper.getSportUrl(sport, SportUrlParam.Calendar));

            if (interval) {
                result = append('sports-soon', interval.title, this.urlHelper.getSportUrl(sport, interval.name, SportUrlParam.Competitions));
            }

            return result;
        }

        return;
    };

    private addRegion: CrumbFunction = (append, sport, route) => {
        if (!route.region) {
            return;
        }

        const region = this.getRegion(sport, route.region);

        if (!region) {
            return;
        }

        return append('theme-competitions-all', region.name, this.urlHelper.getSportUrl(sport, route.context, region));
    };

    private addLeague: CrumbFunction = (append, sport, route) => {
        if (!route.league || isArray(route.league)) {
            return;
        }

        const region = this.getRegion(sport, route.region);
        const leagueinfo = this.getLeague(sport, route.league);
        const league = leagueinfo?.urlParam;
        const imageProfile = leagueinfo?.imageProfile;
        const context = route.conference ? SportUrlParam.Betting : route.context;

        if (!league) {
            return;
        }
        if (sport && sport.id && league.id) {
            return append(
                'sports-league',
                league.name,
                this.urlHelper.getSportUrl(sport, context, region, league),
                Number(sport.id),
                Number(league.id),
                imageProfile,
            );
        }

        return append('sports-league', league.name, this.urlHelper.getSportUrl(sport, context, region, league));
    };

    private addConference: CrumbFunction = (append, sport, route) => {
        if (!route.conference || isArray(route.league)) {
            return;
        }

        const region = this.getRegion(sport, route.region);
        const league = this.getLeague(sport, route.league)?.urlParam;
        const conference = this.getConference(sport, route.conference);
        if (!conference) {
            return;
        }

        return append('', conference.name, this.urlHelper.getSportUrl(sport, route.context, region, league, conference));
    };

    private getRegion(sport: CountItem, id?: number): UrlParam | undefined {
        if (id && sport) {
            const type = getItemTypeForSport(sport.id);

            return this.get(children(sport, type), id);
        }

        return;
    }

    private getLeague(sport: CountItem, id?: number): { urlParam: UrlParam; imageProfile?: ImageProfile } | undefined {
        if (id && sport) {
            const childern = children(sport, ItemType.Competition);

            return { urlParam: this.get(childern, id)!, imageProfile: childern.find((x) => x.id === id)?.imageProfile };
        }

        return;
    }

    private getConference(sport: CountItem, id?: number): UrlParam | undefined {
        if (id && sport) {
            return this.get(children(sport, ItemType.Conference), id);
        }

        return;
    }

    private get(source: UrlParam[], id: number): UrlParam | undefined {
        return source.filter((item) => item.id === id).pop();
    }
}
